import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './client';
import type {
  CreateBatchInput,
  MoveBatchInput,
  OfSchedule,
  Planning,
} from './types';

/**
 * Cache d'ETag par fenetre. Le polling envoie `If-None-Match` ; sur un 304, le
 * serveur ne renvoie pas de corps et on ressert la derniere vue connue. C'est ce
 * qui rend un rafraichissement frequent quasi gratuit (plan section 1.8).
 */
const planningCache = new Map<string, { etag: string; data: Planning }>();

async function fetchPlanning(from: string, to: string): Promise<Planning> {
  const url = `/api/planning?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
  const cached = planningCache.get(url);

  const response = await fetch(url, {
    headers: cached ? { 'If-None-Match': cached.etag } : undefined,
  });

  if (response.status === 304 && cached) return cached.data;

  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const err = (payload as { error?: { code: string; message: string } } | null)?.error;
    throw new ApiError(response.status, err?.code ?? 'UNKNOWN', err?.message ?? `Erreur ${response.status}`);
  }

  const data = payload as Planning;
  const etag = response.headers.get('ETag');
  if (etag) planningCache.set(url, { etag, data });
  return data;
}

export function usePlanning(from: string, to: string, options: { refetchMs?: number } = {}) {
  return useQuery({
    queryKey: ['planning', from, to],
    queryFn: () => fetchPlanning(from, to),
    refetchInterval: options.refetchMs ?? 120_000,
    refetchOnWindowFocus: true,
  });
}

function useInvalidatePlanning() {
  const qc = useQueryClient();
  // Une mutation invalide toutes les fenetres ; le cache d'ETag force un vrai
  // refetch (l'ETag serveur aura change).
  return () => qc.invalidateQueries({ queryKey: ['planning'] });
}

export function useCreateBatch() {
  const invalidate = useInvalidatePlanning();
  return useMutation({
    mutationFn: (input: CreateBatchInput) => api.post('/batches', input),
    onSuccess: invalidate,
  });
}

export function useMoveBatch() {
  const invalidate = useInvalidatePlanning();
  return useMutation({
    mutationFn: (input: { id: number; data: MoveBatchInput }) => api.patch(`/batches/${input.id}`, input.data),
    onSuccess: invalidate,
  });
}

export function useDeleteBatch() {
  const invalidate = useInvalidatePlanning();
  return useMutation({
    mutationFn: (id: number) => api.del(`/batches/${id}`),
    onSuccess: invalidate,
  });
}

export function useBatchSchedule(batchId: number | null) {
  return useQuery({
    queryKey: ['batch-schedule', batchId],
    queryFn: () => api.get<OfSchedule[]>(`/batches/${batchId}/schedule`),
    enabled: batchId != null,
  });
}
